import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import type { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js'
import type { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import type { CircleEntity } from '@/plugins/cad/module/CADModule/model/entity'
import { EntityObjectUtils } from '@/plugins/cad/module/CADModule/object/entityObject/entityObjectUtils'
import { createCadLine2Object } from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import { BaseObjectManager, HighlightMode } from './BaseObjectManager'

/** 圆几何体对象管理器 */
export class CircleObjectManager extends BaseObjectManager<CircleEntity, Line2> {
  // 缓存的几何体
  private cachedGeometryMap: Map<string, LineGeometry> = new Map()
  // 已使用缓存几何体的构件
  private usedCacheGeometryEntityMap: Map<string, Set<string>> = new Map()
  // entity的原材质
  private entityOriginalMaterialMap: Map<string, LineMaterial> = new Map()

  protected createObject(entity: CircleEntity) {
    const geometry = this.getCachedGeometry(entity)
    const material = this.getCachedMaterial(entity)
    const object = createCadLine2Object(geometry, material)
    object.position.set(entity.center.x, entity.center.y, 0)
    object.computeLineDistances()
    this.entityOriginalMaterialMap.set(entity.id, object.material)
    return object
  }

  protected onUpdateGeometry(entity: CircleEntity): void {
    const object = this.objectMap.get(entity.id)
    if (!object) {
      return
    }
    this.disposeGeometryFromObject(object)
    object.geometry = this.getCachedGeometry(entity)
    object.position.set(entity.center.x, entity.center.y, 0)
    object.computeLineDistances()
  }

  protected onUpdateMaterial(entity: CircleEntity): void {
    const object = this.objectMap.get(entity.id)
    if (!object) {
      return
    }
    this.disposeMaterialFromObject(object)
    object.material = this.getCachedMaterial(entity)
    object.material.needsUpdate = true

    this.entityOriginalMaterialMap.set(entity.id, object.material)
    this.updateEntityHighlight(entity)
  }

  protected disposeObject(object: Line2): void {
    this.disposeGeometryFromObject(object)
    this.disposeMaterialFromObject(object)
    this.entityOriginalMaterialMap.delete(object.userData.entity.id)
  }

  protected handleEntityHighlight(entity: CircleEntity, mode: HighlightMode): void {
    const object = this.objectMap.get(entity.id)
    if (!object) {
      return
    }
    if (mode === HighlightMode.None) {
      // 取消高亮
      object.material = this.entityOriginalMaterialMap.get(entity.id)!
      object.material.needsUpdate = true
      object.computeLineDistances()
    } else {
      // 高亮
      const material =
        mode === HighlightMode.Hover
          ? this.materialManager.getCachedHoverMaterial(entity)
          : this.materialManager.getCachedSelectMaterial(entity)
      object.material = material
      object.material.needsUpdate = true
      object.computeLineDistances()
    }
  }

  private getCachedGeometryKey(entity: CircleEntity) {
    const segmentsNum = this.resolveSegmentsNum(entity)
    return `${entity.radius}_${segmentsNum}`
  }

  private getCachedGeometry(entity: CircleEntity) {
    const key = this.getCachedGeometryKey(entity)
    if (!this.cachedGeometryMap.has(key)) {
      const geometry = this.createGeometry(entity)
      geometry.userData = { cacheKey: key }
      this.cachedGeometryMap.set(key, geometry)
      geometry.computeBoundingBox()
      geometry.computeBoundingSphere()
    }
    if (!this.usedCacheGeometryEntityMap.has(key)) {
      this.usedCacheGeometryEntityMap.set(key, new Set())
    }
    this.usedCacheGeometryEntityMap.get(key)!.add(entity.id)
    return this.cachedGeometryMap.get(key)!
  }

  private disposeGeometryFromObject(object: Line2) {
    const geometry = object.geometry
    const cacheKey = geometry.userData.cacheKey
    const entity = object.userData.entity as CircleEntity
    const usedSet = this.usedCacheGeometryEntityMap.get(cacheKey)
    usedSet?.delete(entity.id)
    if (usedSet && usedSet.size === 0) {
      this.cachedGeometryMap.delete(cacheKey)
      this.usedCacheGeometryEntityMap.delete(cacheKey)
      geometry.dispose()
    }
  }

  // 创建圆几何体使用原点作为中心点，后续通过对象变换进行位置调整
  private createGeometry(entity: CircleEntity) {
    const points = EntityObjectUtils.getArcSamplePoints(entity, true)
    return DrawUtils.getLineGeometryByPoints(points)
  }

  /** 基于弓高误差计算圆段数 */
  private resolveSegmentsNum(entity: CircleEntity): number {
    const segmentsNum = EntityObjectUtils.getArcSegments(entity)
    entity.segmentsNum = segmentsNum
    return segmentsNum
  }
}
